import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as R from 'remeda';
import { IntlShape, useIntl } from 'react-intl';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import * as z from 'zod';
import { GridSortItem } from '@rossum/components/data-grid/utils';
import { LeavingDialog } from '@rossum/components/leaving-dialog/LeavingDialog';
import { IconAlertTriangle, IconPlus } from '@rossum/ui/icons/tabler';
import { Button, Stack, SvgIcon, Tooltip } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import {
  DataGridPro,
  GridCellParams,
  GridFilterItem,
  GridPaginationModel,
  GridRowClassNameParams,
  GridSortModel,
  useGridApiRef,
} from '@rossum/ui/x-data-grid-pro';
import { logBrowserSearchAttempt } from '../../../lib/gtm';
import { parseJsonPreprocessor } from '../../../lib/parseJsonPreprocessor';
import { useStoredQuery } from '../../../utils/hooks/useStoredQuery';
import { FilterPanel } from '../../document-list-filtering/FilterPanel';
import { FilterColDef } from '../../document-list-filtering/types';
import { mongoDbOperatorsMap } from '../../mql/constants';
import { encodeMqlQuery } from '../../mql/encodeMqlQuery';
import { boolValueOptions, pickFieldsWithCodecTypes } from '../../mql/helpers';
import {
  dateOperators,
  multiSelectOperators,
  numericOperators,
  stringFieldOperators,
} from '../../mql/operators';
import { notify } from '../../toaster';
import { DatasetDetailLayout } from '../dataset-detail/DatasetDetailLayout';
import { ReplaceDataDialog } from '../dataset-import/ReplaceDataDialog';
import { getErrorMessage } from '../getErrorMessage';
import { useDatasetTableState } from '../hooks/useDatasetTableState';
import { useGetDatasetDetails } from '../hooks/useGetDatasetDetails';
import { useGetDatasetRowCount } from '../hooks/useGetDatasetRowCount';
import { isOperationInProgress, useOperations } from '../hooks/useOperations';
import { usePatchDatasetRows } from '../hooks/usePatchDatasetRows';
import { useTable } from '../hooks/useTable';
import { datasetsPath } from '../routes';
import {
  Dataset,
  DatasetColumn,
  getColumnsFromMetadata,
  isEditableColumn,
  isValidMongoKey,
} from '../types';
import {
  buildDuplicatedRowChanges,
  buildNewRowDefaults,
  buildPatchOperations,
  resolveCellEdits,
} from './buildPatchOperations';
import { toDateQueryValue } from './dateUtils';
import { EmptyData } from './EmptyData';
import { NoFilterResultsOverlay } from './NoFilterResultsOverlay';
import { Row, ROW_STATUS, RowEdit } from './types';
import { useColumns } from './useColumns';
import { useRows } from './useRows';

const DEFAULT_PAGE_SIZE = 100;

const pageSizeOptions = [DEFAULT_PAGE_SIZE, 250, 1000];

// The grid identifies rows by the Mongo `_id`. Its default row key (`row.id`)
// is unusable here: `id` is a legal dataset column name, so writing the key
// there would overwrite the user's own `id` values in the grid.
const getRowId = (row: Row): string => row._id;

// The PATCH endpoint accepts 1–100 operations per atomic batch.
const MAX_PATCH_OPERATIONS = 100;

const gridFilterItemSchema = z.object({
  field: z.string(),
  operator: z.string(),
  value: z.unknown().optional(),
  id: z.union([z.string(), z.number()]).optional(),
});

export const datasetsQueryParser = z.object({
  pageSize: z.coerce.number().optional().default(DEFAULT_PAGE_SIZE),
  page: z.coerce.number().optional().default(0),
  filters: z
    .preprocess(parseJsonPreprocessor, z.array(gridFilterItemSchema))
    .optional()
    .default([]),
  sortField: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

const getFilterColDef = (
  column: DatasetColumn,
  intl: IntlShape
): FilterColDef => ({
  field: column.datasetAttributeName,
  headerName: column.name,
  operators:
    column.type === 'date'
      ? [...dateOperators]
      : column.type === 'number'
        ? [...numericOperators]
        : column.type === 'bool'
          ? [...multiSelectOperators]
          : [...stringFieldOperators],
  dataType: column.type,
  ...(column.type === 'bool' ? boolValueOptions(intl) : {}),
});

export const DatasetTable = ({
  table,
  refetch,
}: {
  table: Dataset;
  refetch: () => void;
}) => {
  const intl = useIntl();

  const hasLoggedSearchRef = useRef(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        if (!hasLoggedSearchRef.current) {
          logBrowserSearchAttempt('dataset-table');
          hasLoggedSearchRef.current = true;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const currentDatasetName = table.name;

  // Only manually-uploaded datasets are editable from the UI.
  const isReadOnly = !table.metadata.manual;

  const [parsedParams, setParsedParams] = useStoredQuery({
    parser: datasetsQueryParser,
    localStorageKey: undefined,
  });

  const { entry: tableState, updateEntry: updateTableState } =
    useDatasetTableState(currentDatasetName);

  const apiRef = useGridApiRef();

  const handlePaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      const filters = JSON.stringify(
        parsedParams.filters
      ) as unknown as GridFilterItem[];

      if (newModel.pageSize !== parsedParams.pageSize) {
        setParsedParams({ pageSize: newModel.pageSize, page: 0, filters });
      } else {
        setParsedParams({ ...newModel, filters });
      }
    },
    [parsedParams.filters, parsedParams.pageSize, setParsedParams]
  );

  const tableColumns = useMemo(
    () => getColumnsFromMetadata(table.metadata, table.schema),
    [table.metadata, table.schema]
  );

  const filterColumns = useMemo(
    () =>
      tableColumns
        .filter(c => isValidMongoKey(c.datasetAttributeName))
        .map(c => getFilterColDef(c, intl)),
    [tableColumns, intl]
  );

  const invalidFilterColumns = useMemo(
    () =>
      tableColumns
        .filter(c => !isValidMongoKey(c.datasetAttributeName))
        .map(c => c.name),
    [tableColumns]
  );

  // Columns whose inferred schema accepts `null`; only these may be cleared
  // (emptied) inline — the PATCH endpoint rejects null for the rest.
  const nullableColumns = useMemo(
    () =>
      new Set(
        tableColumns.filter(c => c.nullable).map(c => c.datasetAttributeName)
      ),
    [tableColumns]
  );

  // The columns that can be edited inline. Building edits only from these is
  // what lets us avoid denylisting grid/server bookkeeping keys.
  const editableColumns = useMemo(
    () =>
      new Set(
        tableColumns.filter(isEditableColumn).map(c => c.datasetAttributeName)
      ),
    [tableColumns]
  );

  const handleFilterModelChange = useCallback(
    (newFilters: GridFilterItem[]) => {
      setParsedParams({
        page: 0,
        filters: JSON.stringify(newFilters) as unknown as GridFilterItem[],
      });
    },
    [setParsedParams]
  );

  const handleClearAllFilters = useCallback(() => {
    setParsedParams({
      page: 0,
      filters: [],
    });
  }, [setParsedParams]);

  const validFilters = useMemo(
    () => parsedParams.filters.map(f => ({ ...f, isValid: true })),
    [parsedParams.filters]
  );

  const fieldTypes = useMemo(
    () =>
      Object.fromEntries(
        tableColumns.map(c => [c.datasetAttributeName, c.type])
      ),
    [tableColumns]
  );

  const find = useMemo(() => {
    // Date fields must be sent as MongoDB Extended JSON ({ $date }) so the
    // backend compares them as dates; other field types pass through unchanged.
    const normalizedFilters = parsedParams.filters.map(filter =>
      fieldTypes[filter.field] === 'date' && typeof filter.value === 'string'
        ? { ...filter, value: toDateQueryValue(filter.value) }
        : filter
    );
    const mql = encodeMqlQuery(normalizedFilters, {
      codecFieldTypes: pickFieldsWithCodecTypes(fieldTypes),
      operatorEncoders: mongoDbOperatorsMap,
    });
    return mql.$and.length > 0 ? { $and: mql.$and } : {};
  }, [parsedParams.filters, fieldTypes]);

  const { data: operations } = useOperations({
    datasetName: table.name,
    limit: 1,
    enabled: !!table.name,
  });

  const lastOperation = operations?.[0];

  const datasetUploadInProgress = isOperationInProgress(lastOperation);

  // ___ inline row editing ___

  // Pending-edit overlay: only the delta against the server data, keyed by row
  // id. Saving and grid-merge both read straight from this — no pristine-row
  // cache and no diffing at save time.
  const [edits, setEdits] = useState<Map<string, RowEdit>>(new Map());
  // When a save is submitted we remember the id of the latest operation at that
  // moment. The save runs as a new async dataset operation, so we only treat a
  // FINISHED/FAILED list entry as "our save done" once the latest operation id
  // has moved past this baseline — otherwise a previously-finished operation
  // would clear the overlay before our save even started.
  const [saveBaselineOpId, setSaveBaselineOpId] = useState<
    string | null | undefined
  >(undefined);
  const isSavePending = saveBaselineOpId !== undefined;

  const { mutate: patchDatasetRows, isLoading: isSaving } =
    usePatchDatasetRows();

  const hasPendingChanges = edits.size > 0;
  // The PATCH request is in flight or its async operation hasn't terminated yet.
  const saving = isSaving || isSavePending;

  const clearEdits = useCallback(() => {
    setEdits(new Map());
    setSaveBaselineOpId(undefined);
  }, []);

  // The dataset's async operations (upload/replace/save) are observed via the
  // polled operations list. A single effect reacts to the latest operation
  // reaching a terminal state — the only genuinely external signal here, since
  // it's polled query state changing over time rather than a discrete event:
  //   - always refetch the grid once an operation FINISHES;
  //   - if it's our pending save (a newer operation than the one latest at
  //     submit time), also drop the optimistic overlay (or surface a failure).
  // Reusing the list keeps the in-progress/finished state shared across users
  // and refresh-durable, at the cost of the n-1 concurrency edge (acceptable
  // until real collaborative editing).
  useEffect(() => {
    if (lastOperation?.status === 'FINISHED') {
      refetch();
    }

    const isOurSave =
      isSavePending &&
      lastOperation !== undefined &&
      lastOperation._id !== saveBaselineOpId;
    if (!isOurSave) return;

    if (lastOperation.status === 'FINISHED') {
      clearEdits();
    } else if (lastOperation.status === 'FAILED') {
      notify.error({
        title: intl.formatMessage({
          id: 'features.datasets.notification.patchFailed',
        }),
      });
      clearEdits();
    }
  }, [
    lastOperation,
    refetch,
    isSavePending,
    saveBaselineOpId,
    clearEdits,
    intl,
  ]);

  const editingDisabled = datasetUploadInProgress || saving || isReadOnly;

  const newRowCounter = useRef(0);

  const handleAddRow = useCallback(() => {
    newRowCounter.current += 1;
    const id = `new-${newRowCounter.current}`;
    const changes = buildNewRowDefaults(tableColumns);
    setEdits(prev => new Map(prev).set(id, { kind: 'new', changes }));
  }, [tableColumns]);

  const handleDeleteRow = useCallback((row: Row) => {
    setEdits(prev => new Map(prev).set(row._id, { kind: 'deleted' }));
  }, []);

  const handleDuplicateRow = useCallback(
    (row: Row) => {
      newRowCounter.current += 1;
      const id = `new-${newRowCounter.current}`;
      const changes = buildDuplicatedRowChanges(tableColumns, row);
      setEdits(prev => new Map(prev).set(id, { kind: 'new', changes }));
    },
    [tableColumns]
  );

  const handleRestoreRow = useCallback((row: Row) => {
    setEdits(prev => {
      const next = new Map(prev);
      next.delete(row._id);
      return next;
    });
  }, []);

  // Stable grid callbacks so DataGridPro doesn't re-run them for every cell on
  // unrelated renders (the grid is not virtualized, so all rows are live DOM).
  const getRowClassName = useCallback((params: GridRowClassNameParams<Row>) => {
    if (params.row[ROW_STATUS] === 'deleted') return 'row-deleted';
    if (params.row[ROW_STATUS] === 'new') return 'row-new';
    return '';
  }, []);

  const getCellClassName = useCallback(
    (params: GridCellParams<Row>) => {
      const edit = edits.get(params.row._id);
      return edit?.kind === 'edited' && params.field in edit.changes
        ? 'cell-edited'
        : '';
    },
    [edits]
  );

  const isCellEditable = useCallback(
    (params: GridCellParams<Row>) =>
      !editingDisabled && params.row[ROW_STATUS] !== 'deleted',
    [editingDisabled]
  );

  const operationCount = useMemo(
    () => buildPatchOperations(edits).length,
    [edits]
  );

  const tooManyChanges = operationCount > MAX_PATCH_OPERATIONS;

  const handleSave = useCallback(() => {
    const patchOps = buildPatchOperations(edits);
    if (patchOps.length === 0) return;
    if (patchOps.length > MAX_PATCH_OPERATIONS) {
      notify.error({
        title: intl.formatMessage(
          { id: 'features.datasets.data.edit.tooManyChanges' },
          { max: MAX_PATCH_OPERATIONS }
        ),
      });
      return;
    }

    // Remember the latest operation id now; the save creates a new operation
    // and we clear the overlay once a newer one finishes (see the effect above).
    setSaveBaselineOpId(lastOperation?._id ?? null);

    patchDatasetRows(
      { datasetName: table.name, operations: patchOps },
      {
        onSuccess: () => {
          notify.default({
            title: intl.formatMessage({
              id: 'features.datasets.notification.patchStarted',
            }),
          });
        },
        onError: (error: unknown) => {
          // The async operation never started — drop the pending state so the
          // overlay stays editable.
          setSaveBaselineOpId(undefined);
          notify.error({
            title:
              getErrorMessage(error) ??
              intl.formatMessage({
                id: 'features.datasets.notification.patchFailed',
              }),
          });
        },
      }
    );
  }, [edits, patchDatasetRows, table.name, intl, lastOperation]);

  const { columns, actionsField } = useColumns({
    table,
    editingDisabled,
    onDeleteRow: handleDeleteRow,
    onDuplicateRow: handleDuplicateRow,
    onRestoreRow: handleRestoreRow,
  });

  const sortItem: GridSortItem | null = useMemo(
    () =>
      parsedParams.sortField && parsedParams.sortOrder
        ? { field: parsedParams.sortField, sort: parsedParams.sortOrder }
        : null,
    [parsedParams.sortField, parsedParams.sortOrder]
  );

  const sortModel = useMemo<GridSortModel>(
    () => (sortItem ? [sortItem] : []),
    [sortItem]
  );

  const handleSortModelChange = useCallback(
    (sortModelList: GridSortModel) => {
      const model = sortModelList[0];
      const filters = JSON.stringify(
        parsedParams.filters
      ) as unknown as GridFilterItem[];

      if (model && model.sort) {
        setParsedParams({
          ...parsedParams,
          sortField: model.field,
          sortOrder: model.sort,
          filters,
        });
      } else {
        setParsedParams({
          ...parsedParams,
          sortField: undefined,
          sortOrder: undefined,
          filters,
        });
      }
    },
    [parsedParams, setParsedParams]
  );

  const handleColumnOrderChange = useCallback(() => {
    const order = apiRef.current?.getAllColumns().map(c => c.field);
    if (order) {
      updateTableState({ columnOrder: order });
    }
  }, [apiRef, updateTableState]);

  const { data: apiResponse, isLoading } = useGetDatasetDetails({
    name: currentDatasetName,
    find,
    sort: sortItem
      ? { [sortItem.field]: sortItem.sort === 'asc' ? 1 : -1 }
      : undefined,
    skip: parsedParams.page * parsedParams.pageSize,
    limit: parsedParams.pageSize,
    lastSuccessOperation:
      lastOperation?.status === 'FINISHED' ? lastOperation : undefined,
  });

  const { data: rowCount, isLoading: isLoadingRowCount } =
    useGetDatasetRowCount({
      name: currentDatasetName,
      find,
      lastSuccessOperation:
        lastOperation?.status === 'FINISHED' ? lastOperation : undefined,
    });
  // MDH cannot add rows to a dataset with none yet: its PATCH requires a
  // non-empty dataset. Counted without filters (the same query when none are
  // set), and unknown counts as empty.
  const { data: totalRowCount } = useGetDatasetRowCount({
    name: currentDatasetName,
    find: {},
    lastSuccessOperation:
      lastOperation?.status === 'FINISHED' ? lastOperation : undefined,
  });
  const canAddRows = totalRowCount !== undefined && totalRowCount > 0;

  const data = apiResponse?.results;

  // Pristine server rows by id, used at edit time to diff committed cells
  // against the original.
  const baseRowsById = useMemo<Map<string, Row>>(
    () =>
      new Map(
        (data ?? []).map(row => {
          const id = String(row['_id']);
          return [id, { ...row, _id: id, [ROW_STATUS]: 'idle' }];
        })
      ),
    [data]
  );

  const processRowUpdate = useCallback(
    (newRow: Row): Row => {
      const existing = edits.get(newRow._id);
      const isNew = existing?.kind === 'new';

      const original = isNew
        ? { _id: newRow._id, [ROW_STATUS]: 'new' as const }
        : baseRowsById.get(newRow._id);
      if (!original) return newRow;

      const existingChanges =
        existing?.kind === 'edited' || existing?.kind === 'new'
          ? existing.changes
          : {};
      const changes = resolveCellEdits(
        newRow,
        original,
        existingChanges,
        nullableColumns,
        editableColumns
      );
      const hasChanges = !R.isEmpty(changes);

      setEdits(prev => {
        const next = new Map(prev);
        if (isNew) {
          next.set(newRow._id, { kind: 'new', changes });
        } else if (hasChanges) {
          next.set(newRow._id, { kind: 'edited', changes });
        } else {
          next.delete(newRow._id);
        }
        return next;
      });

      if (isNew) {
        return { ...original, ...changes, [ROW_STATUS]: 'new' };
      }
      return hasChanges
        ? { ...original, ...changes, [ROW_STATUS]: 'edited' }
        : { ...original, [ROW_STATUS]: 'idle' };
    },
    [baseRowsById, edits, nullableColumns, editableColumns]
  );

  const hasActiveFilters = parsedParams.filters.length > 0;

  // Without filters the count is the whole dataset; an empty page alone (e.g.
  // past the last one) does not make the dataset empty.
  const isDatasetEmpty = !hasActiveFilters && rowCount === 0;
  // A dataset with columns keeps its grid when empty, showing its columns; one
  // without has nothing to show until data is uploaded.
  const showEmptyPage = isDatasetEmpty && tableColumns.length === 0;

  // Owned here rather than by the prompt, which the grid remounts as its
  // overlay changes.
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const openUpload = isReadOnly ? undefined : () => setUploadDialogOpen(true);

  const emptyRowsOverlay = useCallback(
    () => (
      <EmptyData
        datasetUploadInProgress={datasetUploadInProgress}
        onUpload={isReadOnly ? undefined : () => setUploadDialogOpen(true)}
      />
    ),
    [datasetUploadInProgress, isReadOnly]
  );

  const { rows } = useRows({ edits, data });

  const showNoFilterResults =
    !isLoading && hasActiveFilters && rows.length === 0;

  return (
    <DatasetDetailLayout
      table={table}
      tab="data"
      fullWidth
      hasPendingChanges={hasPendingChanges}
      save={{
        label: intl.formatMessage({ id: 'features.datasets.data.edit.save' }),
        dataCy: 'save-changes-button',
        disabled: editingDisabled || !hasPendingChanges,
        loading: saving,
        onClick: handleSave,
      }}
    >
      {!showEmptyPage ? (
        <>
          <Stack px={2} pt={2} direction="row" alignItems="center">
            {filterColumns.length !== tableColumns.length ? (
              <Tooltip
                title={intl.formatMessage(
                  {
                    id: 'features.datasets.data.filtering.invalidColumns',
                  },
                  {
                    columns: invalidFilterColumns.join(', '),
                  }
                )}
              >
                <SvgIcon color="warning" sx={{ mx: 1 }}>
                  <IconAlertTriangle />
                </SvgIcon>
              </Tooltip>
            ) : null}
            <FilterPanel
              columns={filterColumns}
              handleFilterModelChange={handleFilterModelChange}
              handleClearAllFilters={handleClearAllFilters}
              validFilters={validFilters}
              invalidFilters={[]}
              hasActiveQueue={false}
            />
            {canAddRows ? (
              <Button
                data-cy="add-row-button"
                color="secondary"
                variant="text"
                disabled={editingDisabled || tooManyChanges}
                startIcon={
                  <SvgIcon>
                    <IconPlus />
                  </SvgIcon>
                }
                onClick={handleAddRow}
                sx={{ ml: 'auto' }}
              >
                {intl.formatMessage({
                  id: 'features.datasets.data.edit.addRow',
                })}
              </Button>
            ) : null}
          </Stack>

          <Stack
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: borderRadius('l'),
            }}
            m={2}
          >
            {showNoFilterResults ? (
              <NoFilterResultsOverlay onClearFilters={handleClearAllFilters} />
            ) : (
              <DataGridPro
                key={currentDatasetName}
                apiRef={apiRef}
                disableRowSelectionExcludeModel
                disableVirtualization
                autoHeight
                columns={columns}
                rows={rows}
                getRowId={getRowId}
                loading={isLoading}
                // _____editing_____
                editMode="cell"
                processRowUpdate={processRowUpdate}
                // processRowUpdate is synchronous and never rejects; this
                // handler only suppresses MUI's default console error.
                onProcessRowUpdateError={() => undefined}
                isCellEditable={isCellEditable}
                getRowClassName={getRowClassName}
                getCellClassName={getCellClassName}
                // Pin via initialState (uncontrolled) — passing pinnedColumns
                // as a controlled prop re-applies it on every render, and when
                // it lands in the same transaction as a rowCount change (e.g.
                // the refetch after a save), DataGridPro throws "You're not
                // allowed to update several sub-state in one transaction".
                initialState={{
                  pinnedColumns: { right: [actionsField] },
                  ...(tableState?.columnOrder
                    ? { columns: { orderedFields: tableState.columnOrder } }
                    : {}),
                }}
                onColumnOrderChange={handleColumnOrderChange}
                // _____pagination_____
                pagination
                paginationMode="server"
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={pageSizeOptions}
                paginationModel={{
                  page: parsedParams.page,
                  pageSize: parsedParams.pageSize,
                }}
                // _____sorting_____
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                rowCount={rowCount ?? 0}
                disableRowSelectionOnClick
                disableColumnSelector
                slots={
                  isDatasetEmpty ? { noRowsOverlay: emptyRowsOverlay } : {}
                }
                slotProps={{
                  basePagination: {
                    // @ts-expect-error wrong type
                    labelRowsPerPage: intl.formatMessage({
                      id: 'containers.allDocuments.pagination.label',
                    }),
                    sx: {
                      // hide pagination when there are no rows
                      pointerEvents:
                        isLoading || isLoadingRowCount ? 'none' : 'inherit',
                      justifyContent: 'flex-end',
                      display: rowCount === 0 ? 'none' : 'inherit',
                      [`> * p`]: {
                        margin: 0,
                      },
                    },
                  },
                }}
                sx={{
                  '&': {
                    // Room for the empty dataset's upload prompt.
                    ...(isDatasetEmpty
                      ? { '--DataGrid-overlayHeight': '300px' }
                      : {}),
                    '--DataGrid-pinnedBackground': t =>
                      t.palette.background.paper,
                    '--DataGrid-containerBackground': t =>
                      t.palette.background.paper,
                  },
                  // Only the changed cells are highlighted (dashed border); the
                  // edited row itself is not tinted.
                  '& .cell-edited': {
                    border: t => `1px dashed ${t.palette.warning.main}`,
                  },
                  // The grid's edit input is a bare InputBase with no border, so
                  // the Mui-error class (set when a JSON cell holds invalid
                  // JSON) has nothing to style by default. Give it a visible red
                  // outline + text so the blocked commit is obvious.
                  '& .MuiDataGrid-editInputCell.Mui-error': {
                    outline: t => `1px solid ${t.palette.error.main}`,
                    color: t => t.palette.error.main,
                  },
                  // Dim the data cells of a deleted row, but NOT via opacity on
                  // the whole row — opacity creates a stacking context that
                  // children can't escape, so the restore action would look
                  // disabled. Style the data cells directly and exclude the
                  // actions cell.
                  [`& .row-deleted .MuiDataGrid-cell:not([data-field="${actionsField}"])`]:
                    {
                      opacity: 0.5,
                      textDecoration: 'line-through',
                    },
                  // Outline client-side new rows so they stand out from the
                  // server data until saved.
                  '& .row-new .MuiDataGrid-cell:not(.MuiDataGrid-cell--pinnedRight)':
                    {
                      border: t => `1px dashed ${t.palette.success.main}`,
                    },
                  px: 4,
                  py: 3,
                }}
                localeText={{
                  footerTotalRows: intl.formatMessage({
                    id: 'components.dataGrid.footerTotalRows',
                  }),
                }}
              />
            )}
          </Stack>
        </>
      ) : (
        <EmptyData
          datasetUploadInProgress={datasetUploadInProgress}
          onUpload={openUpload}
        />
      )}
      <ReplaceDataDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        table={table}
        hasRows={false}
      />
      <LeavingDialog when={hasPendingChanges} />
    </DatasetDetailLayout>
  );
};

export const DatasetTableWrapper = ({
  match: {
    params: { id },
  },
}: RouteComponentProps<{ id: string }>) => {
  const { data: table, refetch, isSuccess, isFetching } = useTable(id);
  // A deleted dataset leaves the list as soon as its delete is accepted. A
  // cached list may predate a new dataset, so wait for the refetch.
  if (isSuccess && !isFetching && !table)
    return <Redirect to={datasetsPath()} />;
  return table ? <DatasetTable table={table} refetch={refetch} /> : null;
};
